import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type {
  Collection,
  CollectionTree,
  CollectionQuery,
  CreateCollectionInput,
  UpdateCollectionInput,
  AddModelsToCollectionInput,
  RemoveModelsFromCollectionInput,
} from '@maker-toolchest/shared';
import { collectionsApi, type PaginatedResult } from './api';
import { modelKeys } from '../models/hooks';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const collectionKeys = {
  all: ['collections'] as const,
  lists: () => [...collectionKeys.all, 'list'] as const,
  list: (query?: CollectionQuery) => [...collectionKeys.lists(), query] as const,
  trees: () => [...collectionKeys.all, 'tree'] as const,
  tree: () => [...collectionKeys.trees()] as const,
  details: () => [...collectionKeys.all, 'detail'] as const,
  detail: (id: string) => [...collectionKeys.details(), id] as const,
};

// ============================================================================
// COLLECTION HOOKS
// ============================================================================

export function useCollections(
  query?: CollectionQuery,
  options?: Omit<UseQueryOptions<PaginatedResult<Collection>>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: collectionKeys.list(query),
    queryFn: () => collectionsApi.list(query),
    placeholderData: (previousData) => previousData,
    ...options,
  });
}

export function useCollectionTree(
  options?: Omit<UseQueryOptions<CollectionTree[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: collectionKeys.tree(),
    queryFn: () => collectionsApi.getTree(),
    ...options,
  });
}

export function useCollection(
  id: string,
  options?: Omit<UseQueryOptions<CollectionTree>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: collectionKeys.detail(id),
    queryFn: () => collectionsApi.getById(id),
    enabled: !!id,
    ...options,
  });
}

export function useCreateCollection(
  options?: UseMutationOptions<Collection, Error, CreateCollectionInput>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: collectionsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: collectionKeys.trees() });
    },
    ...options,
  });
}

export function useUpdateCollection(
  options?: UseMutationOptions<
    Collection,
    Error,
    { id: string; input: UpdateCollectionInput }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }) => collectionsApi.update(id, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: collectionKeys.trees() });
    },
    ...options,
  });
}

export function useDeleteCollection(options?: UseMutationOptions<void, Error, string>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: collectionsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: collectionKeys.trees() });
    },
    ...options,
  });
}

// ============================================================================
// MODEL OPERATIONS
// ============================================================================

export function useAddModelsToCollection(
  options?: UseMutationOptions<
    void,
    Error,
    { collectionId: string; input: AddModelsToCollectionInput }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ collectionId, input }) => collectionsApi.addModels(collectionId, input),
    onSuccess: (_, variables) => {
      // Invalidate collection details (for updated modelCount)
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(variables.collectionId) });
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: collectionKeys.trees() });
      // Invalidate model lists (models now belong to this collection)
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
      // Invalidate individual model details for all affected models
      variables.input.modelIds.forEach((modelId) => {
        queryClient.invalidateQueries({ queryKey: modelKeys.detail(modelId) });
      });
    },
    ...options,
  });
}

export function useRemoveModelsFromCollection(
  options?: UseMutationOptions<
    void,
    Error,
    { collectionId: string; input: RemoveModelsFromCollectionInput }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ collectionId, input }) => collectionsApi.removeModels(collectionId, input),
    onSuccess: (_, variables) => {
      // Invalidate collection details (for updated modelCount)
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(variables.collectionId) });
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: collectionKeys.trees() });
      // Invalidate model lists (models no longer belong to this collection)
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
      // Invalidate individual model details for all affected models
      variables.input.modelIds.forEach((modelId) => {
        queryClient.invalidateQueries({ queryKey: modelKeys.detail(modelId) });
      });
    },
    ...options,
  });
}

// Re-export types for convenience
export type { CollectionQuery };
